/**
 * ResidualPhaseRail — left-side navigation for the six rail stops.
 *
 * Visual grammar borrowed verbatim from
 * `features/athena/components/WorkflowV2StepRail` (raised --card rail,
 * 3px --foreground left bar on the selected row, muted hover wash)
 * so the v2 surface reads as part of the same UI family.
 *
 * Status model (status indicator inside the numbered circle):
 *   • complete  — index < activeIndex
 *   • active    — index === activeIndex (backend's current stop)
 *   • locked    — index > activeIndex (future, click-locked)
 *
 * `selectedStop` is the user's nav selection (sticky); it's marked
 * with the active-row wash + left bar. The active stop separately
 * gets a coloured circle. The two states are independent so the
 * user can scroll back through past stops without losing track of
 * where the backend currently is.
 */
import React from 'react';
import { Icon } from '../../../../components/ui/Icon';
import {
  PHASE_TO_RAIL_STOP,
  RAIL_STOPS,
  RAIL_STOP_LABEL,
  RailStop,
  ResidualPhase,
  railStopIndex,
} from '../../state/types';
import styles from './ResidualPhaseRail.module.css';

interface ResidualPhaseRailProps {
  backendPhase: ResidualPhase | null;
  selectedStop: RailStop | null;
  onSelect: (stop: RailStop) => void;
}

type StopStatus = 'complete' | 'active' | 'locked';

const stopStatus = (
  backendPhase: ResidualPhase | null,
  stop: RailStop,
): StopStatus => {
  if (!backendPhase) return 'locked';
  const activeStop = PHASE_TO_RAIL_STOP[backendPhase];
  const activeIdx = railStopIndex(activeStop);
  const thisIdx = railStopIndex(stop);
  if (thisIdx < activeIdx) return 'complete';
  if (thisIdx === activeIdx) return 'active';
  return 'locked';
};

export default function ResidualPhaseRail({
  backendPhase,
  selectedStop,
  onSelect,
}: ResidualPhaseRailProps) {
  return (
    <aside className={styles.rail}>
      <div className={styles.head}>
        <h2 className={styles.title}>Residual TARA</h2>
      </div>
      <ul className={styles.list}>
        {RAIL_STOPS.map((stop, i) => {
          const status = stopStatus(backendPhase, stop);
          const isSelected = selectedStop === stop;
          const isLocked = status === 'locked';
          const cls = [
            styles.item,
            isSelected ? styles.itemSelected : '',
            isLocked ? styles.itemLocked : '',
          ]
            .filter(Boolean)
            .join(' ');
          const circleCls = [
            styles.stepNum,
            status === 'complete' ? styles.stepNumComplete : '',
            status === 'active' ? styles.stepNumActive : '',
            status === 'locked' ? styles.stepNumLocked : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={stop}>
              <button
                type="button"
                className={cls}
                onClick={() => {
                  if (isLocked) return;
                  onSelect(stop);
                }}
                disabled={isLocked}
                aria-current={isSelected ? 'page' : undefined}
              >
                <span className={circleCls}>
                  {status === 'complete' && <Icon name="check" size={14} />}
                  {status === 'active' && <span>{i + 1}</span>}
                  {status === 'locked' && <Icon name="lock" size={12} />}
                </span>
                <span className={styles.label}>{RAIL_STOP_LABEL[stop]}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
